import TableHeader from "./TableHeader"
import { Button, PageButton } from "./Button"
import { SortIcon, SortUpIcon, SortDownIcon } from "./SortTableIcons"
import { Loader } from "@mantine/core"
import { Icon } from "@material-tailwind/react"
import useFetchAndLoad from "hooks/useFetchAndLoad.ts"

import {
  ChevronDoubleLeftIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDoubleRightIcon,
} from "@heroicons/react/solid"
import { useTable, useGlobalFilter, useFilters, useSortBy, usePagination } from "react-table"

const Table = ({
  columns,
  data,
  iconName,
  header,
  editable,
  setOpened,
  setIsCreation,
  nested,
  loading,
  isAccessesTab,
  refreshData,
}) => {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state,
    preGlobalFilteredRows,
    setGlobalFilter,
  } = useTable(
    {
      columns,
      data,
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination
  )

  return (
    <>
      <TableHeader
        preGlobalFilteredRows={preGlobalFilteredRows}
        globalFilter={state.globalFilter}
        setGlobalFilter={setGlobalFilter}
        iconName={iconName}
        headerGroups={headerGroups}
        header={header}
        editable={editable}
        setOpened={setOpened}
        setIsCreation={setIsCreation}
        nested={nested}
        isAccessesTab={isAccessesTab}
        refreshData={refreshData}
      />
      {loading && (
        <div className='flex justify-center items-center m-5 overflow-auto'>
          <Loader size='xl' variant='dots' className='mx-2' color={"#212529"} />
        </div>
      )}

      {/* Table Body */}
      <div className='flex justify-left items-center shadow-lg bg-white m-5 overflow-auto'>
        <table className='min-w-full divide-y divide-gray-200' {...getTableProps()}>
          <thead className='bg-gray-50'>
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => (
                  <th
                    className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell'
                    {...column.getHeaderProps(column.getSortByToggleProps())}>
                    <div className='flex items-center justify-between'>
                      {column.render("Header")}
                      {column.id !== "Acciones" && (
                        <span>
                          {column.isSorted ? (
                            column.isSortedDesc ? (
                              <SortDownIcon className='w-4 h-4 text-gray-400' />
                            ) : (
                              <SortUpIcon className='w-4 h-4 text-gray-400' />
                            )
                          ) : (
                            <SortIcon className='w-4 h-4 text-gray-400' />
                          )}
                        </span>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody className='bg-white divide-y divide-gray-200' {...getTableBodyProps()}>
            {page.map((row, i) => {
              prepareRow(row)
              return (
                <tr {...row.getRowProps()}>
                  {row.cells.map((cell, cellIndex) => {
                    return (
                      <td
                        className='flex justify-between px-6 py-4 whitespace-nowrap text-sm text-gray-900 w-full lg:w-auto lg:table-cell lg:static'
                        {...cell.getCellProps()}>
                        <div
                          className='text-gray-600 lg:hidden top-0 left-0 px-2 py-1 text-xs font-bold uppercase'
                          {...headerGroups[0].headers[cellIndex].getHeaderProps(
                            headerGroups[0].headers[cellIndex].getSortByToggleProps()
                          )}>
                          {
                            <>
                              <div className='flex items-center justify-between'>
                                {headerGroups[0].headers[cellIndex].render("Header")}
                                {headerGroups[0].headers[cellIndex].id !== "Acciones" && (
                                  <span className='ml-2'>
                                    {headerGroups[0].headers[cellIndex].isSorted ? (
                                      headerGroups[0].headers[cellIndex].isSortedDesc ? (
                                        <SortDownIcon className='w-4 h-4 text-gray-400' />
                                      ) : (
                                        <SortUpIcon className='w-4 h-4 text-gray-400' />
                                      )
                                    ) : (
                                      <SortIcon className='w-4 h-4 text-gray-400' />
                                    )}
                                  </span>
                                )}
                              </div>
                            </>
                          }
                        </div>
                        <div>{cell.render("Cell")}</div>
                      </td>
                    )
                  })}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {!loading && data.length < 1 ? (
        <div className='flex justify-center items-center shadow-lg bg-white m-5 overflow-auto'>
          <div className='flex items-center rounded-full bg-gray-600 p-1'>
            <Icon name={"close"} color={"white"} />
          </div>
          <h4 className='uppercase text-gray-900 font-bold tracking-wider m-4 text-lg'>
            Sin datos
          </h4>
        </div>
      ) : (
        <></>
      )}

      {/* Table Footer */}
      <div className='py-3 flex items-center justify-between text-gray-900 font-bold tracking-wider ml-6 mr-5 text-lg'>
        <div className='flex-1 flex justify-between sm:hidden '>
          <Button
            className={`${!canPreviousPage ? "opacity-50" : ""} ml-1`}
            onClick={() => previousPage()}
            disabled={!canPreviousPage}>
            Anterior
          </Button>
          <Button
            className={`${!canNextPage ? "opacity-50" : ""} mr-1`}
            onClick={() => nextPage()}
            disabled={!canNextPage}>
            Siguiente
          </Button>
        </div>
        <div className='hidden sm:flex-1 sm:flex sm:items-center sm:justify-between'>
          <div className='flex items-center gap-x-2'>
            <span className='text-sm text-gray-700'>
              Página <span className='font-medium'>{state.pageIndex + 1}</span> of{" "}
              <span className='font-medium'>{pageOptions.length}</span>
            </span>
            <select
              className='border rounded px-1 py-1 text-gray-600 leading-tight focus:outline-none focus:shadow-outline text-sm shadow-sm'
              value={state.pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value))
              }}>
              {[5, 10, 15].map((pageSize) => (
                <option key={pageSize} value={pageSize}>
                  Mostrar {pageSize}
                </option>
              ))}
            </select>
          </div>
          <div>
            <nav
              className='relative z-0 inline-flex rounded-md shadow-sm -space-x-px'
              aria-label='Pagination'>
              <PageButton
                id='first-page'
                className={`rounded-l ${!canPreviousPage ? "opacity-50" : ""}`}
                onClick={() => gotoPage(0)}
                disabled={!canPreviousPage}>
                <span className='sr-only'>First</span>
                <ChevronDoubleLeftIcon className='h-5 w-5' aria-hidden='true' />
              </PageButton>

              <PageButton
                className={`${!canPreviousPage ? "opacity-50" : ""}`}
                onClick={() => previousPage()}
                disabled={!canPreviousPage}>
                <span className='sr-only'>Previous</span>
                <ChevronLeftIcon className='h-5 w-5' aria-hidden='true' />
              </PageButton>
              <PageButton
                className={`${!canNextPage ? "opacity-50" : ""}`}
                onClick={() => nextPage()}
                disabled={!canNextPage}>
                <span className='sr-only'>Next</span>
                <ChevronRightIcon className='h-5 w-5' aria-hidden='true' />
              </PageButton>
              <PageButton
                id='last-page'
                className={`rounded-r ${!canNextPage ? "opacity-50" : ""}`}
                onClick={() => gotoPage(pageCount - 1)}
                disabled={!canNextPage}>
                <span className='sr-only'>Last</span>
                <ChevronDoubleRightIcon className='h-5 w-5' aria-hidden='true' />
              </PageButton>
            </nav>
          </div>
        </div>
      </div>
    </>
  )
}

export default Table
